import Vue from 'vue'
import applogger from '@applogger'

const state = () => ({
  config: null,
  fields: null,
  types: null,
  taxonomy: null,
  promise: null
})

const actions = {
  init({commit, state, rootGetters}) {
    if (process.env.SERVER_BUILD !== 'ssr') {
      try {
        const version = localStorage.getItem('IncipereVersion')
        if (version !== process.env.BUILDVERSION) {
          localStorage.clear()
          localStorage.setItem('IncipereVersion', process.env.BUILDVERSION)
        }
        if (typeof window !== 'undefined' && typeof document !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search)
          if (urlParams.has('source')) {
            document.cookie = `referrerSource=${urlParams.get('source')}; max-age=315360000; path=/`
          }
        }
      }
      catch (err) {
        // No-op
      }
    }
    if (!state.promise) {
      state.promise = Promise.all(Object.entries(cacheItems).map(([key, cacheinfo]) => {
        if (!state[cacheinfo.state]) {
          return this.dispatch('loadItemFromRemote', { url: key, config: rootGetters.AuthorizedRequestConfig() })
            .then(data => commit(cacheinfo.mutation, data))
        }
      }))
        .then(_ => {
          applogger.debug('Configuration loaded')
          return true
        })
    }
    return state.promise
  },
  reloadConfig({commit, rootGetters}) {
    const cacheinfo = cacheItems['/vuejs/config']
    const key = '/vuejs/config'
    return this.dispatch('loadItemFromRemote', {
      url: key,
      queryStr: { reset: true },
      config: rootGetters.AuthorizedRequestConfig()
    })
      .then(data => commit(cacheinfo.mutation, data))
  },
  loadContentTypes: function({ rootGetters, commit }, payload) {
    if ((payload && !payload.force) && state.types) {
      return Promise.resolve(state.types)
    }

    const requestPromise = rootGetters['getGraphQLClient']().query({
      query: `query {
        contenttypes: __type (name:"ContentType" ) {
          enumValues {
            name
            description
          }
        }
      }`
    })
      .then(response => {
        if (response.data && response.data.contenttypes.enumValues) {
          const contentTypes = response.data.contenttypes.enumValues.reduce((acc, { name, description }) => ({ ...acc, [name]: description }), {})
          commit('SET_CONTENT_TYPES', contentTypes)
          return contentTypes
        }
        return []
      })
    commit('SET_CONTENT_TYPES', requestPromise)
    return requestPromise
  }

}

const mutations = {
  SET_CONFIG(state, data) {
    state.config = data
  },
  SET_TAXONOMY(state, data) {
    state.taxonomy = data
  },
  SET_FIELD_INFO(state, data) {
    state.fields = data
  },
  SET_CONTENT_TYPES: function(state, types) {
    if (typeof types.then !== 'function') {
      state.types = {}
      Object.entries(types).forEach(([typeID, typeName]) => {
        Vue.set(state.types, typeID, typeName)
      })
    }
    else {
      state.types = types
    }
  }
}

const getters = {
  getCookie() {
    return function(name) {
      if (typeof document === 'undefined') return null

      const cookie = {}

      document.cookie.split(';').forEach(function(el) {
        const [k, v] = el.split('=')
        cookie[k.trim()] = v
      })

      return (Object.keys(cookie).includes(name)) ? cookie[name] : null
    }
  },
  getFilestore(state) {
    return function(name) {
      if (state.filestores[name] !== undefined) {
        return {
          endpoint: state.filestores[name].endpoint,
          filestoreID: state.filestores[name].filestoreID
        }
      }
      else {
        throw new Error('Access to unknown filestore: ' + name)
      }
    }
  },
  variable_get(state) {
    return function(varname) {
      return state.config.data[varname]
    }
  },
  fieldValue(state) {
    return function(node, fieldname) {
      return (node) ? state.fields.data[node.type][fieldname].allowed_values[node[fieldname]] : ''
    }
  },
  contentTypeName(state) {
    return (typename) => {
      if (!state.types || typeof state.types.then === 'function') {
        return 'unknown'
      }
      return Object.prototype.hasOwnProperty.call(state.types, typename) ? state.types[typename] : 'unknown'
    }
  },
  getTermLabelFromEnum(state) {
    return (tid) => {
      if (tid) {
        let id = tid
        if (id.startsWith('_')) {
          id = id.substr(1)
        }
        const term = state.taxonomy.data.terms[id]
        if (term) {
          return term.name || null
        }
        else {
          return null
        }
      }
      return null
    }
  },
  getTermDescriptionFromEnum(state) {
    return (tid) => {
      if (tid) {
        let id = tid
        if (id.startsWith('_')) {
          id = id.substr(1)
        }
        const term = state.taxonomy.data.terms[id]
        if (term) {
          return term.description || null
        }
        else {
          return null
        }
      }
      return null
    }
  }
}

const cacheItems = {
  '/vuejs/config': {
    cacheName: 'localStorage',
    scope: 'global',
    ttl: 86400,
    cache_keys: {
      specific: ['global-config']
    },
    refresh: 'stale-revalidate',
    refreshArgs: [true],
    state: 'config',
    mutation: 'SET_CONFIG'
  },
  '/vuejs/taxonomy': {
    cacheName: 'localStorage',
    scope: 'global',
    ttl: 86400,
    refresh: 'always',
    cache_keys: {
      type: [],
      specific: ['taxonomy-terms']
    },
    refreshArgs: [true],
    state: 'taxonomy',
    mutation: 'SET_TAXONOMY'
  },
  '/vuejs/field-info': {
    cacheName: 'localStorage',
    scope: 'global',
    ttl: 86400,
    cache_keys: {
      type: [],
      specific: ['field-info']
    },
    refresh: 'always',
    refreshArgs: [true],
    state: 'fields',
    mutation: 'SET_FIELD_INFO'
  }

}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
